// Ref: https://raw.githubusercontent.com/chakra-ui/chakra-ui/main/packages/theme/src/components/modal.ts

import { PartsStyleObject } from '@/util/chakra';
import { modalAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    defineStyle,
} from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';

const { defineMultiStyleConfig, definePartsStyle } =
    createMultiStyleConfigHelpers(parts.keys);

const baseStyleOverlay = defineStyle({
    bg: 'blackAlpha.600',
    zIndex: 'modal',
});

const baseStyleDialogContainer = defineStyle((props) => {
    const { isCentered, scrollBehavior } = props;

    return {
        display: 'flex',
        zIndex: 'modal',
        justifyContent: 'center',
        alignItems: isCentered ? 'center' : 'flex-start',
        overflow: scrollBehavior === 'inside' ? 'hidden' : 'auto',
    };
});

const baseStyleDialog = defineStyle((props) => {
    const { scrollBehavior, maxH } = props;

    return {
        borderRadius: 'md',
        bg: mode('white', 'gray.700')(props),
        color: 'inherit',
        my: { base: 10, sm: '10rem' },
        zIndex: 'modal',
        maxH:
            maxH ||
            (scrollBehavior === 'inside'
                ? 'min(600px, calc(100% - 12rem))'
                : undefined),
        boxShadow: mode('lg', 'dark-lg')(props),
    };
});

const baseStyleHeader = defineStyle({
    px: 10,
    pt: 8,
    pb: 2,
});

const baseStyleCloseButton = defineStyle({
    position: 'absolute',
    top: 2,
    insetEnd: 3,
});

const baseStyleBody = defineStyle((props) => {
    const { scrollBehavior } = props;
    return {
        px: 10,
        pt: 12,
        pb: 10,
        flex: 1,
        overflow: scrollBehavior === 'inside' ? 'auto' : undefined,
        borderRadius: 'md',
    };
});

const baseStyleFooter = defineStyle({
    px: 10,
    py: 12,
});

const baseStyle = definePartsStyle((props) => ({
    overlay: baseStyleOverlay,
    dialogContainer: baseStyleDialogContainer(props),
    dialog: baseStyleDialog(props),
    header: baseStyleHeader,
    closeButton: baseStyleCloseButton,
    body: baseStyleBody(props),
    footer: baseStyleFooter,
}));

/**
 * Since the `maxWidth` prop references theme.sizes internally,
 * we can leverage that to size our modals.
 */
function getSize(value: string): PartsStyleObject<typeof parts> {
    if (value === 'full') {
        return {
            dialog: {
                maxW: '100vw',
                minH: '100vh',
                '@supports(min-height: -webkit-fill-available)': {
                    minH: '-webkit-fill-available',
                },
                my: 0,
                borderRadius: 0,
            },
        };
    }
    return {
        dialog: { maxW: value },
    };
}

const sizes = {
    xs: getSize('xs'),
    sm: getSize('sm'),
    md: getSize('md'),
    lg: getSize('lg'),
    xl: getSize('xl'),
    '2xl': getSize('2xl'),
    '3xl': getSize('3xl'),
    '4xl': getSize('4xl'),
    '5xl': getSize('5xl'),
    '6xl': getSize('6xl'),
    full: getSize('full'),
};

const modalTheme = defineMultiStyleConfig({
    baseStyle,
    sizes,
    defaultProps: {
        size: 'md',
    },
});

export default modalTheme;
